import { ChevronDown } from "lucide-react";
import { useId, type ReactNode } from "react";

import { cn } from "@/lib/utils";

type Props = {
  name: string;
  title: string;
  hint: string;
  checked: boolean;
  onToggle: () => void;
  children: ReactNode;
};

export function PickChoice({
  name,
  title,
  hint,
  checked,
  onToggle,
  children,
}: Props) {
  const panelId = useId();
  const closeIfOpen = (e: React.SyntheticEvent) => {
    if (!checked) return;
    e.preventDefault();
    onToggle();
  };
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[10px] border transition-colors duration-150",
        checked
          ? "border-indigo-400 bg-indigo-500/[0.04]"
          : "border-slate-300 dark:border-slate-700",
      )}
    >
      <label
        onClick={closeIfOpen}
        aria-expanded={checked}
        aria-controls={panelId}
        className="grid cursor-pointer grid-cols-[18px_minmax(0,1fr)_16px] items-start gap-2.5 px-3 py-2.5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500"
      >
        <input
          type="radio"
          name={name}
          checked={checked}
          onChange={onToggle}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              if (checked) closeIfOpen(e);
              else if (e.key === "Enter") onToggle();
            }
          }}
          className="mt-[3px] accent-indigo-600"
        />
        <span className="flex flex-col gap-0.5">
          <span className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">
            {title}
          </span>
          <span className="text-xs text-slate-600 dark:text-slate-400">
            {hint}
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            "mt-0.5 h-3.5 w-3.5 text-slate-500 motion-safe:transition-transform motion-safe:duration-150 motion-safe:ease-[cubic-bezier(.23,1,.32,1)]",
            checked && "rotate-180 text-indigo-600 dark:text-indigo-300",
          )}
        />
      </label>
      <div
        id={panelId}
        className={cn(
          checked &&
            "flex flex-col gap-2.5 border-t border-slate-200 p-3 dark:border-slate-800",
        )}
      >
        {checked && children}
      </div>
    </div>
  );
}
